<script setup lang="ts">
  import type { CardsSliderStoryblok } from '@/storyblok/component-types-sb'
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import '@/assets/css/swiper.css'
  import { SCREEN_SM, SCREEN_MD } from '@/tailwind.config'

  const CAROUSEL_NEXT = 'carousel-next'
  const CAROUSEL_PREV = 'carousel-prev'
  const CAROUSEL_PAGINATION = 'carousel-pagination'

  const props = withDefaults(
    defineProps<{
      blok: CardsSliderStoryblok
      loop?: boolean
    }>(),
    {
      loop: false
    }
  )

  const hasExactlyTwoCards = computed(() => props.blok?.cards?.length === 2)
  const { isDesktop } = useBTBreakpoints()
  const canNavigate = ref(false)

  const paginationSettings = computed(() => ({
    el: `#${CAROUSEL_PAGINATION}-${props.blok._uid}`,
    clickable: true,
    bulletActiveClass: 'border-bt-blue-dark',
    renderBullet: (index: number, className: string) => {
      return `<span class="${className} w-8 lg:w-14 border-b-4 border-border block h-1 cursor-pointer"></span>`
    }
  }))

  const navigationSettings = computed(() => ({
    nextEl: `#${CAROUSEL_NEXT}-${props.blok._uid}`,
    prevEl: `#${CAROUSEL_PREV}-${props.blok._uid}`
  }))

  const sliderSettings = computed<SwiperSettings>(() => {
    if (props.blok.singleSlidePerPage) {
      return {
        speed: 500,
        spaceBetween: 16,
        slidesPerView: 1,
        slidesPerGroup: 1,
        breakpoints: {
          [SCREEN_SM]: { spaceBetween: 24 },
          [SCREEN_MD]: {
            spaceBetween: 60
          }
        },
        autoHeight: true,
        loop: props.loop,
        navigation: navigationSettings.value,
        pagination: paginationSettings.value,
        keyboard: true,
        watchSlidesProgress: true
      }
    }

    return {
      speed: 500,
      spaceBetween: 16,
      slidesPerView: 1.1,
      slidesPerGroup: 1,
      breakpoints: {
        [SCREEN_SM]: { slidesPerView: 2.5, slidesPerGroup: 2 },
        [SCREEN_MD]: {
          slidesPerView: hasExactlyTwoCards.value ? 2 : 3,
          slidesPerGroup: hasExactlyTwoCards.value ? 2 : 3,
          spaceBetween: 32
        }
      },
      loop: props.loop,
      navigation: navigationSettings.value,
      pagination: paginationSettings.value,
      keyboard: true,
      watchSlidesProgress: true
    }
  })

  function handleCanNavigateChange(nextValue: boolean) {
    canNavigate.value = nextValue
  }

  const showNavigation = computed(() => props.blok.showNavigationOnDesktop && isDesktop.value && canNavigate.value)
</script>

<template>
  <section class="relative md:mx-auto md:w-10/12 lg:w-full">
    <div
      class="cards-slider max-w-screen relative inset-x-1/2 -mx-screen-1/2 w-screen overflow-x-clip sm:pb-4 md:left-auto md:right-auto md:mx-0 md:max-w-full lg:-mx-8 lg:my-9 lg:overflow-visible"
    >
      <div
        class="px-4"
        :class="blok.singleSlidePerPage ? 'sm:px-5 lg:px-0' : 'sm:px-6 lg:px-1'"
      >
        <LazyUiSliderComponent
          :slider-settings="sliderSettings"
          :slides="blok.cards"
          slide-class="h-auto"
          @swipercannavigatechange="handleCanNavigateChange"
        >
          <template #slide="{ slide: card }">
            <StoryblokComponent
              :blok="card"
              :layout-variant="hasExactlyTwoCards ? 'wide' : 'stacked'"
            />
          </template>

          <template #slider-container-end>
            <div
              :id="`${CAROUSEL_PAGINATION}-${blok._uid}`"
              class="cards-pagination mt-1 items-center justify-center gap-4 py-2 sm:mt-4"
            />
          </template>
        </LazyUiSliderComponent>
      </div>
    </div>

    <UiSliderNavigationRoundArrows
      v-show="showNavigation"
      class="absolute -left-12 top-[calc(50%_-_2.5rem)] z-10 hidden w-[calc(100%+6rem)] justify-between min-[1280px]:flex min-[1416px]:hidden min-[1552px]:flex"
      :prev-btn-id="`${CAROUSEL_PREV}-${blok._uid}`"
      :next-btn-id="`${CAROUSEL_NEXT}-${blok._uid}`"
    />
  </section>
</template>

<style scoped lang="scss">
  :deep(.cards-slider) {
    swiper-container::part(container) {
      @apply overflow-visible lg:overflow-x-clip lg:px-8;
    }

    .swiper-slide-visible > div {
      @apply shadow-slider-lg;
    }

    .cards-pagination {
      @apply hidden;
    }

    .cards-pagination:has(.swiper-pagination-bullet:nth-child(2)) {
      @apply flex;
    }
  }
</style>
